import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApplication, ICreateApplication, ICreatePasswordCredential } from '../types/applications';

@Injectable({
    providedIn: 'root'
})
export class ApplicationService{
    private readonly http = inject(HttpClient);
    private readonly baseUrl: string = 'api/applications';

    /**
     * Header recognised by the backend on GET /api/applications/{id}. When set to "true" the
     * per-application authorization check tolerates Microsoft Graph read-after-write replication
     * lag (the user's brand-new appRoleAssignment may not yet be visible). The frontend should set
     * this only when navigating directly from a successful application-creation flow.
     */
    public static readonly NEWLY_CREATED_HEADER = 'X-Ccms-Newly-Created';

    public getApplications(query: string | undefined): Observable<IApplication[]> {
        if (query) {
            const searchQueryEncoded = query ? encodeURIComponent(query) : '';
            return this.http.get<IApplication[]>(this.baseUrl + '?query=' + searchQueryEncoded);
        } else {
            return this.http.get<IApplication[]>(this.baseUrl);
        }
    }

    public getApplicationByAppId(appId: string, freshlyCreated: boolean = false): Observable<IApplication> {
        const options = freshlyCreated
            ? { headers: new HttpHeaders({ [ApplicationService.NEWLY_CREATED_HEADER]: 'true' }) }
            : {};
        return this.http.get<IApplication>(this.baseUrl + `/${appId}`, options);
    }

    public createApplication(data: ICreateApplication): Observable<IApplication> {
        return this.http.post<IApplication>(this.baseUrl, data);
    }

    public addNewClientSecret(applicationId: string, body: ICreatePasswordCredential) {
        return this.http.patch<string>(`${this.baseUrl}/${applicationId}`, body);
    }

    public deleteClientSecret(applicationId: string, keyId: string): Observable<IApplication> {
        return this.http.delete<IApplication>(`${this.baseUrl}/${applicationId}/clientCredentials/${keyId}`);
    }

}
